import { TASK_PAGE_NAV_GROUPS, type TaskPageNavGroup } from '@erp/shared-types';

/**
 * Pure helpers for the "Sidebar section" picker shared by the task page and
 * agent page editors. A page is placed in exactly one of: a built-in
 * route-derived section (`navGroup`), an org-created page section
 * (`pageSectionId`), or — both null — the generic "Pages" fallback. The picker
 * flattens both kinds into one option list, so option ids are prefixed to tell
 * them apart. Kept free of React so it stays trivially unit-testable.
 */

export type PagePlacement = {
  navGroup: TaskPageNavGroup | null;
  pageSectionId: string | null;
};

export type PagePlacementOption = {
  id: string;
  label: string;
  /** Picker group heading: built-in sections vs. the org's own sections. */
  group: string;
};

const NAV_GROUP_OPTION_PREFIX = 'nav-group:';
const PAGE_SECTION_OPTION_PREFIX = 'page-section:';

export function toNavGroup(value: string | null): TaskPageNavGroup | null {
  return TASK_PAGE_NAV_GROUPS.find((group) => group === value) ?? null;
}

/** The picker option id for a placement, or null for the "Pages" fallback. */
export function placementOptionId(placement: PagePlacement): string | null {
  if (placement.pageSectionId !== null) {
    return `${PAGE_SECTION_OPTION_PREFIX}${placement.pageSectionId}`;
  }
  if (placement.navGroup !== null) {
    return `${NAV_GROUP_OPTION_PREFIX}${placement.navGroup}`;
  }
  return null;
}

/** The placement a picked option id stands for (cleared = the fallback). */
export function placementFromOptionId(
  optionId: string | null | undefined,
): PagePlacement {
  if (optionId?.startsWith(PAGE_SECTION_OPTION_PREFIX)) {
    return {
      navGroup: null,
      pageSectionId: optionId.slice(PAGE_SECTION_OPTION_PREFIX.length),
    };
  }
  if (optionId?.startsWith(NAV_GROUP_OPTION_PREFIX)) {
    return {
      navGroup: toNavGroup(optionId.slice(NAV_GROUP_OPTION_PREFIX.length)),
      pageSectionId: null,
    };
  }
  return { navGroup: null, pageSectionId: null };
}

/**
 * Picker options: the org's own sections first (they are the ones an admin
 * created on purpose), then the built-in sidebar sections.
 */
export function buildPlacementOptions({
  pageSections,
  builtInGroupLabel,
  customGroupLabel,
}: {
  pageSections: ReadonlyArray<{ id: string; name: string }>;
  builtInGroupLabel: string;
  customGroupLabel: string;
}): PagePlacementOption[] {
  const custom = pageSections.map((section) => ({
    id: `${PAGE_SECTION_OPTION_PREFIX}${section.id}`,
    label: section.name,
    group: customGroupLabel,
  }));
  const builtIn = TASK_PAGE_NAV_GROUPS.map((group) => ({
    id: `${NAV_GROUP_OPTION_PREFIX}${group}`,
    label: group,
    group: builtInGroupLabel,
  }));
  return [...custom, ...builtIn];
}

/**
 * The label the admin tables show for a page's placement: its org-created
 * section's name, else its built-in section, else the "Pages" fallback label.
 */
export function placementLabel({
  placement,
  sectionNameById,
  fallbackLabel,
}: {
  placement: { navGroup: string | null; pageSectionId: string | null };
  sectionNameById: ReadonlyMap<string, string>;
  fallbackLabel: string;
}): string {
  const sectionName =
    placement.pageSectionId !== null
      ? sectionNameById.get(placement.pageSectionId)
      : undefined;
  return sectionName ?? placement.navGroup ?? fallbackLabel;
}
